import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../axiosInstance";

const BASE_URL = "/api/v1/invoices";

// Create Invoice
export const createInvoice = createAsyncThunk(
  "invoice/createInvoice",
  async ({ orderId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${BASE_URL}`, { orderId });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// Get Single Invoice by ID
export const getInvoice = createAsyncThunk(
  "invoice/getInvoice",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${BASE_URL}/${invoiceId}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// Get Full Bank Statement
export const getInvoiceStatement = createAsyncThunk(
  "invoice/getInvoiceStatement",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${BASE_URL}/${invoiceId}/statement`);
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// Record Payment
export const recordPayment = createAsyncThunk(
  "invoice/recordPayment",
  async ({ invoiceId, amount, paidAt, note }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${BASE_URL}/${invoiceId}/pay`, {
        amount,
        paidAt,
        note,
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// Get Seller/Buyer Based Invoice
export const getSellerInvoice = createAsyncThunk(
  "invoice/getSellerInvoice",
  async ({ order, seller, buyer }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${BASE_URL}/get/invoice`, {
        order,
        seller,
        buyer,
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// Run Cron Manually
export const runInterestCron = createAsyncThunk(
  "invoice/runInterestCron",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${BASE_URL}/cron/run`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// Get All Invoices of Logged-in Seller
export const getSellerAllInvoices = createAsyncThunk(
  "invoice/getSellerAllInvoices",
  async ({ id, page = 1, limit = 10, status }, { rejectWithValue }) => {
    try {
      let url = `${BASE_URL}/seller/${id}?page=${page}&limit=${limit}`;

      // status only when required
      if (status && status !== "Default") {
        url += `&status=${status}`;
      }

      const { data } = await axios.get(url);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);
// Get All Invoices of Logged-in Buyer
export const getBuyerAllInvoices = createAsyncThunk(
  "invoice/getBuyerAllInvoices",
  async ({ id, page = 1, limit = 10, status }, { rejectWithValue }) => {
    try {
      let url = `${BASE_URL}/buyer/${id}?page=${page}&limit=${limit}`;

      // status only when required
      if (status && status !== "Default") {
        url += `&status=${status}`;
      }

      const { data } = await axios.get(url);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// Update Credit (Buyer)
export const updateCredit = createAsyncThunk(
  "invoice/updateCredit",
  async ({ bankStatementId, credit }, { rejectWithValue }) => {
    try {
      const { data } = await axios.patch(
        `${BASE_URL}/buyer/credit/${bankStatementId}`,
        { credit }
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// Update Payment Status (Seller)
export const updatePaymentStatus = createAsyncThunk(
  "invoice/updatePaymentStatus",
  async ({ bankStatementId, paymentStatus }, { rejectWithValue }) => {
    try {
      await axios.patch(`${BASE_URL}/seller/status/${bankStatementId}`, {
        paymentStatus,
      });
      return { _id: bankStatementId, paymentStatus };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Update failed" }
      );
    }
  }
);

// Delete Bank Statement Entry (Buyer Mode)
export const deleteBankStatement = createAsyncThunk(
  "invoice/deleteBankStatement",
  async ({ invoiceId, bankStatementId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${BASE_URL}/buyer/bank-statement`, {
        data: {
          invoiceId,
          bankStatementId,
        },
      });
      return data.data; // return updated statement
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

// Get Seller/Buyer Invoices with Pagination
export const getSellerBuyerInvoices = createAsyncThunk(
  "invoice/getSellerBuyerInvoices",
  async (
    { seller, buyer, page = 1, limit = 10 },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await axios.post(
        `${BASE_URL}/buyer`, 
        {
          seller,
          buyer,
          page,
          limit,
        }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);


// Slice
const invoiceSlice = createSlice({
  name: "invoice",
  initialState: {
    invoice: null,
    statement: [],
    sellerInvoice: null,
    loading: false,
    error: null,
    message: null,
    sellerInvoices: [],
    pagination: {
      totalInvoices: 0,
      currentPage: 1,
      limit: 10,
      totalPages: 1,
    },
    buyerInvoices: [],
    buyerPagination: {
      totalInvoices: 0,
      currentPage: 1,
      limit: 10,
      totalPages: 1,
    },
    sellerBuyerInvoices: [],
  sellerBuyerPagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  },
  },

  reducers: {
    clearInvoiceState(state) {
      state.invoice = null;
      state.statement = [];
      state.sellerInvoice = null;
      state.error = null;
      state.message = null;
    },
    // clearSellerBuyerInvoices: (state) => {
    //   state.sellerBuyerInvoices = [];
    //   state.sellerBuyerPagination = {};
    // },
    clearSellerBuyerInvoices: (state) => {
  state.sellerBuyerInvoices = [];
  state.sellerBuyerPagination = {
    totalInvoices: 0,
    currentPage: 1,
    limit: 10,
    totalPages: 1,
  };
},

  },

  extraReducers: (builder) => {
    builder
      // Create Invoice
      .addCase(createInvoice.pending, (state) => {
        state.loading = true;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoice = action.payload.data;
        state.message = action.payload.message;
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Get Invoice
      .addCase(getInvoice.pending, (state) => {
        state.loading = true;
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoice = action.payload.data;
      })
      .addCase(getInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Statement
      .addCase(getInvoiceStatement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoiceStatement.fulfilled, (state, action) => {
        state.loading = false;
        state.statement = action.payload;
      })
      .addCase(getInvoiceStatement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Statement fetch failed";
      })

      // Record Payment
      .addCase(recordPayment.pending, (state) => {
        state.loading = true;
      })
      .addCase(recordPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.invoice = action.payload.data;
        state.message = action.payload.message;
      })
      .addCase(recordPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Seller Invoice
      .addCase(getSellerInvoice.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSellerInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.sellerInvoice = action.payload.data;
        state.message = action.payload.message;
      })
      .addCase(getSellerInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Run Cron
      .addCase(runInterestCron.pending, (state) => {
        state.loading = true;
      })
      .addCase(runInterestCron.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
      })
      .addCase(runInterestCron.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Get All Seller Invoices
      .addCase(getSellerAllInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSellerAllInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.sellerInvoices = action?.payload?.data;
        state.message = action.payload?.message;
        state.pagination = action.payload?.pagination;
      })
      .addCase(getSellerAllInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Something went wrong";
      })

      // Get All Buyer Invoices
      .addCase(getBuyerAllInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBuyerAllInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.buyerInvoices = action.payload?.data || [];
        state.buyerPagination = action.payload?.pagination;
        state.message = action.payload?.message;
      })
      .addCase(getBuyerAllInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Something went wrong";
      })
      // Buyer updateCredit
      .addCase(updateCredit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCredit.fulfilled, (state, action) => {
        state.loading = false;
        // Update statement array
        state.statement = state.statement.map((item) =>
          item._id === action.payload._id ? action.payload : item
        );
        state.message = "Credit updated successfully";
      })
      .addCase(updateCredit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Credit update failed";
      })
      // Seller updatePaymentStatus
      .addCase(updatePaymentStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePaymentStatus.fulfilled, (state, action) => {
        state.loading = false;

        state.statement = state.statement.map((item) =>
          item._id === action.payload._id
            ? { ...item, paymentStatus: action.payload.paymentStatus }
            : item
        );

        state.message = "Payment status updated successfully";
      })
      .addCase(updatePaymentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Payment status update failed";
      })

      // Delete Bank Statement Entry// Inside extraReducers
      .addCase(deleteBankStatement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBankStatement.fulfilled, (state, action) => {
        state.loading = false;
        state.statement = action.payload; 
        state.message = "Bank statement entry deleted successfully";
      })
      .addCase(deleteBankStatement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Deletion failed";
      })
      // Get Seller/Buyer Invoices (Paginated)
.addCase(getSellerBuyerInvoices.pending, (state) => {
  state.loading = true;
  state.error = null;
})
.addCase(getSellerBuyerInvoices.fulfilled, (state, action) => {
  state.loading = false;
  state.sellerBuyerInvoices = action.payload?.data || [];
  state.sellerBuyerPagination = action.payload?.pagination;
  state.message = action.payload?.message;
})
.addCase(getSellerBuyerInvoices.rejected, (state, action) => {
  state.loading = false;
  state.error = action.payload?.message || "Failed to fetch invoices";
})

  },
});

export const { clearInvoiceState, clearSellerBuyerInvoices } = invoiceSlice.actions;

export default invoiceSlice.reducer;
